import type { PropTypes } from './SimpleNotification.types';
import type { FunctionComponent } from 'react';

import { useToast } from '@rango-dev/whale/lab';
import { useEffect, useId, useRef } from 'react';

const AUTO_HIDE_DURATION = 3_000;

/**
 * Declarative wrapper over the Radix-based Toast: mirrors an `open` boolean into
 * a single toast. Each instance owns a unique toast id, so notifications sharing
 * the same message across components (e.g. multiple wallet rows) never collide
 * or dismiss one another. Kept as a thin adapter so existing call sites keep
 * their props; placement props (`vertical`/`horizontal`/`style`) are no-ops now
 * that the Toast owns a fixed viewport (bottom-right desktop / bottom mobile).
 */
export const SimpleNotification: FunctionComponent<PropTypes> = (props) => {
  const { toast, dismiss } = useToast();
  const toastId = useId();
  const openedRef = useRef(false);

  useEffect(() => {
    if (props.open) {
      openedRef.current = true;
      toast({
        id: toastId,
        title: props.message,
        type: props.severity,
        duration: props.autoHideDuration ?? AUTO_HIDE_DURATION,
        showCloseButton: props.hasCloseIcon,
        onOpenChange: (open) => {
          if (!open) {
            props.setOpen(false);
          }
        },
      });
    } else if (openedRef.current) {
      // Only dismiss a toast this instance actually opened.
      dismiss(toastId);
    }
    // Fire only when the open flag flips, mirroring the previous behaviour.
  }, [props.open]);

  return null;
};
